
import {
  BookOpen,
  Calendar,
  Clock,
  Mail,
  Pencil,
  Spade,
  Trash2,
  Zap,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetTitle,
} from '@/components/ui/sheet';
import { discoverDialogCloseClass } from '@/components/help-center/DiscoverDialogClose';
import { StaffXatRankBadge } from '@/components/help-center/equipe-shared';
import { PainelActivityTimeline, PAINEL_ACTIVITY_RESOURCE_META } from '@/components/painel/PainelActivityTimeline';
import {
  painelButtonDestructiveClass,
  painelButtonPrimaryClass,
  painelInsetCardClass,
  painelSurfaceAccentClass,
} from '@/components/painel/painel-styles';
import { PainelRocketScroll } from '@/components/painel/PainelRocketScroll';
import { PainelUserAvatar } from '@/components/painel/PainelUserAvatar';
import {
  getPainelActivityLog,
  getUserCollaborationStats,
} from '@/lib/painel-activity-log';
import { resolvePainelUserXatRank } from '@/lib/painel-user-xat-rank';
import {
  PAINEL_ROLE_LABELS,
  isAbsoluteAdminUser,
  type PainelMockUser,
  type PainelRole,
} from '@/lib/painel-rbac';
import { cn } from '@/lib/utils';

function formatDate(iso: string) {
  try {
    return new Intl.DateTimeFormat('pt-BR', { dateStyle: 'medium', timeStyle: 'short' }).format(
      new Date(iso)
    );
  } catch {
    return iso;
  }
}

function StatusBadge({ status }: { status: PainelMockUser['status'] }) {
  const active = status === 'active';
  return (
    <span
      className={cn(
        'inline-flex rounded-full px-2.5 py-0.5 font-inter text-[11px] font-semibold',
        active
          ? 'bg-emerald-500/15 text-emerald-300 ring-1 ring-emerald-400/25'
          : 'bg-slate-500/15 text-slate-400 ring-1 ring-white/10'
      )}
    >
      {active ? 'Ativo' : 'Inativo'}
    </span>
  );
}

function StatHighlight({
  label,
  value,
  icon: Icon,
  accentClass,
}: {
  label: string;
  value: number;
  icon: typeof BookOpen;
  accentClass: string;
}) {
  return (
    <div className={cn(painelInsetCardClass, 'relative overflow-hidden p-3 sm:p-4')}>
      <div
        className="pointer-events-none absolute -right-4 -top-4 h-16 w-16 rounded-full bg-sky-500/10 blur-2xl"
        aria-hidden
      />
      <div className="relative flex items-start justify-between gap-3">
        <div>
          <p className="font-inter text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">
            {label}
          </p>
          <p className="mt-1 font-poppins text-2xl font-bold tabular-nums text-white">{value}</p>
        </div>
        <span
          className={cn(
            'inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ring-1 sm:h-10 sm:w-10',
            accentClass
          )}
        >
          <Icon className="h-4 w-4 sm:h-5 sm:w-5" aria-hidden />
        </span>
      </div>
    </div>
  );
}

type PainelUserProfileSheetProps = {
  user: PainelMockUser | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  viewerRole: PainelRole | null;
  canManage: boolean;
  /** Se omitido, segue `canManage`. Conta absoluta nunca deve permitir exclusão. */
  canDelete?: boolean;
  onEdit: (user: PainelMockUser) => void;
  onDelete: (user: PainelMockUser) => void;
};

export function PainelUserProfileSheet({
  user,
  open,
  onOpenChange,
  viewerRole,
  canManage,
  canDelete = canManage,
  onEdit,
  onDelete,
}: PainelUserProfileSheetProps) {
  if (!user) return null;

  const stats = getUserCollaborationStats(user.username);
  const history = getPainelActivityLog({ username: user.username, limit: 12 });
  const showHistory = viewerRole === 'super_admin';
  const xatRank = resolvePainelUserXatRank(user);
  const isAbsoluteAdmin = isAbsoluteAdminUser(user);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className={cn(
          'flex h-full w-full flex-col gap-0 overflow-hidden border-l border-white/10 p-0 sm:max-w-[440px]',
          'bg-gradient-to-b from-blue-950/95 via-[#030023] to-blue-950/95 text-slate-100',
          'shadow-[0_0_0_1px_rgba(56,189,248,0.1),0_28px_64px_-20px_rgba(0,0,0,0.65)] backdrop-blur-xl',
          '[&>button]:hidden'
        )}
      >
        <div
          className="pointer-events-none absolute inset-0 bg-gradient-to-br from-[#030023]/40 via-[#000b6a]/20 to-[#030023]/50"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute inset-x-0 top-0 h-40 bg-[radial-gradient(ellipse_80%_55%_at_50%_-20%,rgba(56,189,248,0.14),transparent)]"
          aria-hidden
        />

        <SheetClose className={cn(discoverDialogCloseClass, 'right-5 top-5')} />
        <SheetTitle className="sr-only">Perfil do usuário — {user.name}</SheetTitle>

        <div className="relative flex min-h-0 flex-1 flex-col">
          <div className="border-b border-white/10 bg-blue-950/20 px-6 pb-5 pt-6">
            <p className="font-inter text-[10px] font-bold uppercase tracking-[0.18em] text-sky-300/55">
              Usuários
            </p>
            <h2 className="mt-1 font-poppins text-xl font-bold text-white">Perfil do usuário</h2>
            <p className="mt-1.5 font-inter text-sm text-slate-400">
              Conta, colaboração e histórico de edições no painel.
            </p>
          </div>

          <PainelRocketScroll
            className='min-h-0 flex-1'
            viewportClassName='px-6 py-5 scroll-smooth'
          >
            <div className={cn(painelSurfaceAccentClass, 'mb-5 overflow-hidden p-5')}>
              <div className="flex items-start gap-4">
                <PainelUserAvatar name={user.name} avatarUrl={user.avatarUrl} large />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="font-poppins text-lg font-bold text-white">{user.name}</h3>
                    <StatusBadge status={user.status} />
                    {xatRank ? <StaffXatRankBadge rank={xatRank} /> : null}
                  </div>
                  <p className="mt-1 font-inter text-sm text-slate-400">@{user.username}</p>
                  <p className="mt-1 font-inter text-sm text-slate-200">
                    {PAINEL_ROLE_LABELS[user.role]}
                  </p>
                  {isAbsoluteAdmin ? (
                    <p className="mt-2 font-inter text-xs text-amber-200/80">
                      Conta admin absoluta — protegida contra edição e exclusão por outros Super Admins.
                    </p>
                  ) : null}
                </div>
              </div>
            </div>

            <div className="mb-5 grid gap-3">
              <div className={cn(painelInsetCardClass, 'flex items-center gap-3 p-4')}>
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-sky-500/15 ring-1 ring-sky-400/25">
                  <Mail className="h-4 w-4 text-sky-300" aria-hidden />
                </span>
                <div className="min-w-0">
                  <p className="font-inter text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">
                    E-mail
                  </p>
                  <p className="mt-0.5 truncate font-inter text-sm text-slate-100">{user.email}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className={cn(painelInsetCardClass, 'p-4')}>
                  <div className="flex items-center gap-2 text-slate-500">
                    <Clock className="h-3.5 w-3.5" aria-hidden />
                    <p className="font-inter text-[10px] font-bold uppercase tracking-[0.14em]">
                      Último login
                    </p>
                  </div>
                  <p className="mt-2 font-inter text-xs leading-relaxed text-slate-200">
                    {formatDate(user.lastLoginAt)}
                  </p>
                </div>
                <div className={cn(painelInsetCardClass, 'p-4')}>
                  <div className="flex items-center gap-2 text-slate-500">
                    <Calendar className="h-3.5 w-3.5" aria-hidden />
                    <p className="font-inter text-[10px] font-bold uppercase tracking-[0.14em]">
                      Criado em
                    </p>
                  </div>
                  <p className="mt-2 font-inter text-xs leading-relaxed text-slate-200">
                    {formatDate(user.createdAt)}
                  </p>
                </div>
              </div>
            </div>

            <div className="mb-5">
              <div className="mb-3 flex items-end justify-between gap-3">
                <div>
                  <p className="font-inter text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500">
                    Colaboração no painel
                  </p>
                  <p className="mt-1 font-inter text-xs text-slate-500">
                    {stats.total} edições registadas em guias, powers e aces.
                  </p>
                </div>
                <span className="rounded-full border border-sky-400/25 bg-sky-500/10 px-2.5 py-1 font-poppins text-xs font-semibold text-sky-200">
                  {stats.total}
                </span>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <StatHighlight
                  label="Guias"
                  value={stats.guides}
                  icon={BookOpen}
                  accentClass={PAINEL_ACTIVITY_RESOURCE_META.guide.accent}
                />
                <StatHighlight
                  label="Powers"
                  value={stats.powers}
                  icon={Zap}
                  accentClass={PAINEL_ACTIVITY_RESOURCE_META.power.accent}
                />
                <StatHighlight
                  label="Aces"
                  value={stats.aces}
                  icon={Spade}
                  accentClass={PAINEL_ACTIVITY_RESOURCE_META.ace.accent}
                />
              </div>
            </div>

            {showHistory ? (
              <div>
                <p className="mb-3 font-inter text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500">
                  Histórico de alterações
                </p>
                <PainelActivityTimeline
                  entries={history}
                  emptyMessage="Nenhuma edição registada ainda para este usuário."
                />
              </div>
            ) : null}
          </PainelRocketScroll>

          {canManage || canDelete ? (
            <div className="relative flex flex-col gap-2 border-t border-white/10 bg-blue-950/25 px-6 py-4">
              {canManage ? (
                <Button
                  className={cn(
                    painelButtonPrimaryClass,
                    'h-11 w-full ring-0 ring-offset-0 focus-visible:ring-2 focus-visible:ring-sky-400/35 focus-visible:ring-offset-0'
                  )}
                  type="button"
                  onClick={() => onEdit(user)}
                >
                  <Pencil className="mr-2 h-4 w-4" />
                  Editar usuário
                </Button>
              ) : null}
              {canDelete ? (
                <Button
                  className={cn(painelButtonDestructiveClass, 'h-11 w-full')}
                  type="button"
                  variant="ghost"
                  onClick={() => onDelete(user)}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Excluir usuário
                </Button>
              ) : null}
            </div>
          ) : null}
        </div>
      </SheetContent>
    </Sheet>
  );
}
